import { SearchBar, SearchBarProps } from '@idexx-laboratories/spot-react';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { KeyCodes } from '../../utils/dom-utils';
import './SearchBarExtended.scss';

interface SearchBarExtendedProps extends SearchBarProps {
  onSearchClick?: (v: string) => void;
  value: string;
}

const SearchBarExtended = (props: SearchBarExtendedProps): React.JSX.Element => {
  const { onSearchClick, value, onSearchSubmit, onChange, disabled, ...rest } = props;

  const [currentValue, setCurrentValue] = useState<string>(value);

  const doSearch = useCallback(
    (v: string) => {
      if (!disabled) {
        setCurrentValue(v.trim());
        onSearchClick?.(v.trim());
        onSearchSubmit?.();
      }
    },
    [disabled, onSearchClick, onSearchSubmit],
  );

  const keydownListener = useCallback(
    (e: any) => {
      if (e.keyCode === KeyCodes.DOM_VK_ESCAPE) {
        setCurrentValue(value);
        e.currentTarget.blur();
        e.stopPropagation();
      } else if (e.keyCode === KeyCodes.DOM_VK_RETURN) {
        doSearch(currentValue);
        e.currentTarget.blur();
        e.stopPropagation();
      }
    },
    [currentValue, doSearch, value],
  );

  const onClearClick = useCallback(() => {
    if (!disabled) {
      doSearch('');
    }
  }, [disabled, doSearch]);

  return (
    <span className={classNames('spot-search-bar_wrapper', { disabled })}>
      <SearchBar
        value={currentValue}
        onChange={e => {
          setCurrentValue(e.target.value);
          onChange?.(e);
        }}
        onSearchSubmit={() => doSearch(currentValue)}
        onKeyDown={keydownListener}
        role='search'
        disabled={disabled}
        {...rest}
      />
      {currentValue && (
        <span
          className={classNames('spot-search-bar__clear', { disabled })}
          role='search-clear'
          onClick={onClearClick}
          data-testid={`${rest['data-testid']}-clear-btn`}
        >
          &#215;
        </span>
      )}
    </span>
  );
};

export default SearchBarExtended;
